import React from 'react'
import { ChevronDown } from 'lucide-react'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

// Clean neutral card. Colour lives in the pane background behind these cards (a
// subtle pastel block), not on the cards themselves, so the sections read as
// tidy panels floating on a soft tinted ground.
export default function PlanCardShell({
  id,
  title,
  description,
  icon,
  headerAction = null,
  titleAction,
  titleActionLabel,
  titleActionDisabled = false,
  collapsible = true,
  defaultCollapsed = true,
  children,
}) {
  const [collapsed, setCollapsed] = React.useState(collapsible ? defaultCollapsed : false)
  const contentId = id ? `${id}-content` : undefined
  const Icon = icon
  const hasContent = Boolean(children)

  return (
    <div id={id}>
      <Card size="sm" style={collapsed ? { paddingTop: '0.5rem', paddingBottom: '0.5rem' } : undefined}>
        <CardHeader className={collapsed || !hasContent ? 'pb-0' : 'pb-2'}>
          <div className="flex items-start justify-between gap-3">
            <div
              role={collapsible ? 'button' : undefined}
              tabIndex={collapsible ? 0 : undefined}
              aria-expanded={collapsible ? !collapsed : undefined}
              aria-controls={collapsible ? contentId : undefined}
              onClick={collapsible ? ((event) => { event.stopPropagation(); setCollapsed((value) => !value) }) : undefined}
              onKeyDown={collapsible ? ((event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                setCollapsed((value) => !value)
              }) : undefined}
              className={cn(
                'flex min-w-0 flex-1 items-start justify-between gap-2 rounded',
                collapsible && 'cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
            >
              <div className="min-w-0 space-y-0.5">
                <CardTitle className="text-base">
                  {titleAction ? (
                    <button
                      type="button"
                      onClick={titleAction}
                      disabled={titleActionDisabled}
                      aria-label={titleActionLabel}
                      className="flex items-center gap-2 rounded text-left hover:underline hover:underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:no-underline"
                    >
                      {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                      <span>{title}</span>
                    </button>
                  ) : (
                    <span className="flex items-center gap-2">
                      {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                      <span>{title}</span>
                    </span>
                  )}
                </CardTitle>
                {description && !collapsed && <CardDescription>{description}</CardDescription>}
              </div>
              {collapsible && (
                <>
                  <ChevronDown className={cn('mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform', collapsed && '-rotate-90')} aria-hidden />
                  <span className="sr-only">{collapsed ? 'Expand' : 'Collapse'} {typeof title === 'string' ? title : 'section'}</span>
                </>
              )}
            </div>
            {headerAction && <div className="mt-0.5 shrink-0">{headerAction}</div>}
          </div>
        </CardHeader>
        {!collapsed && hasContent && <div id={contentId}>{children}</div>}
      </Card>
    </div>
  )
}
